import { GetAdminExpensesResponse } from "@services/models/Billing/AdminExpensesModel";
import { GetAdminAffiliateInvoicesResponse } from "@services/models/Billing/AffiliateInvoicesModel";
import {
  DownloadCadTradeReportRequest,
  DownloadCadTradeReportResponse,
  GetCadTradeReportsResponse,
} from "@services/models/Billing/CadTradeReportsModel";
import { GetConsumerInvoicesApiResponse } from "@services/models/Billing/ConsumerInvoicesModel";
import { GetBillingResponse } from "@services/models/Billing/GetBillingModel";
import { GetPdfRequest } from "@services/models/Billing/GetPdfModel";
import {
  UpdateCountryBillingRequest,
  UpdateCountryBillingResponse,
} from "@services/models/Billing/UpdateCountryBillingModel";
import {
  UpdateInvoiceStatusRequest,
  UpdateInvoiceStatusResponse,
} from "@services/models/Billing/UpdateInvoiceStatusModel";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance, AxiosResponse } from "axios";

const fileNameFromContentDisposition = (
  disposition?: string | null,
): string | null => {
  if (!disposition) return null;

  const encoded = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(disposition);
  if (encoded?.[1]) {
    const value = encoded[1].trim().replace(/^["']|["']$/g, "");
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  const quoted = /filename\s*=\s*"([^"]+)"/i.exec(disposition);
  if (quoted?.[1]) return quoted[1];

  const plain = /filename\s*=\s*([^;]+)/i.exec(disposition);
  const value = plain?.[1]?.trim().replace(/^["']|["']$/g, "");
  return value || null;
};

const pdfDownloadFromResponse = (
  response: AxiosResponse<Blob>,
): { blob: Blob; fileName: string | null } => {
  const disposition = response.headers["content-disposition"];

  return {
    blob: response.data,
    fileName: fileNameFromContentDisposition(
      typeof disposition === "string" ? disposition : null,
    ),
  };
};

class BillingService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getBilling(params?: {
    limit?: number;
    offset?: number;
  }): Promise<GetBillingResponse> {
    try {
      const { limit = 200, offset = 0 } = params || {};

      const response = await this.api.get<GetBillingResponse>(
        `/adminPanel/getBilling`,
        {
          params: {
            limit,
            offset,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getAdminExpenses(page, rows): Promise<GetAdminExpensesResponse> {
    try {
      const response = await this.api.get<GetAdminExpensesResponse>(
        `/adminPanel/getAdminExpenses?page=${page}&rows=${rows}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getConsumerInvoices(
    page,
    rows,
  ): Promise<GetConsumerInvoicesApiResponse> {
    try {
      const response = await this.api.get<GetConsumerInvoicesApiResponse>(
        `/adminPanel/getConsumerInvoices?page=${page}&rows=${rows}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getAdminAffiliateInvoices(params: {
    page: number;
    limit: number;
    search?: string;
    referrerUserId?: string;
    invoiceMonth?: string;
  }): Promise<any> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", params.page.toString());
      queryParams.append("limit", params.limit.toString());
      if (params.search) queryParams.append("search", params.search);
      if (params.referrerUserId)
        queryParams.append("referrerUserId", params.referrerUserId);
      if (params.invoiceMonth)
        queryParams.append("invoiceMonth", params.invoiceMonth);

      const response = await this.api.get<GetAdminAffiliateInvoicesResponse>(
        `/adminPanel/affiliate/invoices?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async generateMonthlyBillingData(payload?: {
    month: string;
  }): Promise<void> {
    try {
      await this.api.post("/adminPanel/generateMonthlyBillingData", payload);
    } catch (error) {
      throw error;
    }
  }

  public async sendBillingPdfEmail(
    data: GetPdfRequest,
  ): Promise<{ blob: Blob; fileName: string | null }> {
    try {
      const response = await this.api.post<Blob>(
        "/adminPanel/sendBillingPdfEmail",
        {
          invoiceId: data.invoiceId,
          userId: data.userId,
          returnPdf: data.returnPdf,
        },
        {
          responseType: "blob",
        },
      );
      return pdfDownloadFromResponse(response);
    } catch (error) {
      throw error;
    }
  }

  public async affiliateBillingPdf(
    data: GetPdfRequest,
  ): Promise<{ blob: Blob; fileName: string | null }> {
    try {
      const response = await this.api.get<Blob>(
        `/adminPanel/affiliate/invoices/pdf?invoiceId=${data.invoiceId}`,
        {
          responseType: "blob",
        },
      );
      return pdfDownloadFromResponse(response);
    } catch (error) {
      throw error;
    }
  }

  public async getConsumerInvoicePdf(data: {
    invoiceId: number;
  }): Promise<{ blob: Blob; fileName: string | null }> {
    try {
      const response = await this.api.get<Blob>(
        `/adminPanel/getConsumerInvoicePdf?invoiceId=${data.invoiceId}`,
        {
          responseType: "blob",
        },
      );
      return pdfDownloadFromResponse(response);
    } catch (error) {
      throw error;
    }
  }

  public async updateMerchantInvoiceStatus(
    data: UpdateInvoiceStatusRequest,
  ): Promise<UpdateInvoiceStatusResponse> {
    try {
      const response = await this.api.put(
        "/adminPanel/updateMerchantInvoiceStatus",
        {
          invoiceId: data.invoiceId,
          status: data.status,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateReferralInvoiceStatus(
    data: UpdateInvoiceStatusRequest,
  ): Promise<UpdateInvoiceStatusResponse> {
    try {
      const response = await this.api.put(
        "/adminPanel/updateReferralInvoiceStatus",
        {
          invoiceId: data.invoiceId,
          status: data.status,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateMerchantLocation(
    data: UpdateCountryBillingRequest,
  ): Promise<UpdateCountryBillingResponse> {
    try {
      const response = await this.api.put(
        "/adminPanel/updateMerchantLocation",
        {
          merchantId: data.merchantId,
          countryId: data.countryId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getCadTradeReports(): Promise<GetCadTradeReportsResponse> {
    try {
      const response = await this.api.get<GetCadTradeReportsResponse>(
        "/adminPanel/getCadTradeReports",
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async downloadCadTradeReport(
    data: DownloadCadTradeReportRequest,
  ): Promise<DownloadCadTradeReportResponse> {
    try {
      const response = await this.api.get<DownloadCadTradeReportResponse>(
        "/adminPanel/downloadCadTradeReport",
        {
          params: {
            month: data.month,
            type: data.type,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const billingServices = new BillingService();
